/**
 * One vocabulary for the Contract area (T-0229, C2-43 / C2-44 / C2-22 /
 * C2-31). Every screen that names a deviation type, a deviation status, a
 * change order or a pulse period reads it from here, so "Unmet Kpi" on one
 * screen and "Unmet KPI" on another cannot drift apart again.
 */

const DEVIATION_TYPE_LABEL: Record<string, string> = {
  missed_milestone: 'Missed milestone',
  unmet_kpi: 'Unmet KPI',
  quality: 'Quality',
  sla_breach: 'SLA breach',
};

/** `unmet_kpi` → "Unmet KPI"; unknown values read as plain words. */
export function deviationTypeLabel(type: string): string {
  return DEVIATION_TYPE_LABEL[type] ?? sentenceCase(type);
}

const DEVIATION_STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_resolution: 'In resolution',
  resolved: 'Resolved',
  waived: 'Waived',
};

/** The one status name per state: in_resolution is always "In resolution". */
export function deviationStatusLabel(status: string): string {
  return DEVIATION_STATUS_LABEL[status] ?? sentenceCase(status);
}

/** CO-001, CO-002, ... from a SOW version_seq (the baseline is v0 and has no code). */
export function changeOrderCode(versionSeq: number): string {
  return `CO-${String(versionSeq).padStart(3, '0')}`;
}

/** "1 criterion", "2 criteria". */
export function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** `snake_case` → "Snake case". */
export function sentenceCase(value: string): string {
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.length === 0 ? '' : words[0]!.toUpperCase() + words.slice(1);
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * The period a pulse covers, in words (C2-31). New drafts are labelled
 * "Week of 5 Oct 2026" by {@link weekCycleLabel}; older pulses carry the
 * "2026-05" label the first build used, which reads as "May 2026". Any
 * other label is the consultant's own and is shown as typed.
 */
export function formatCycleLabel(label: string): string {
  const monthly = /^(\d{4})-(\d{2})$/.exec(label.trim());
  if (monthly) {
    const month = Number(monthly[2]);
    if (month >= 1 && month <= 12) return `${MONTHS[month - 1]} ${monthly[1]}`;
  }
  return label;
}

/**
 * "Week of 5 Oct 2026": the Monday of the week containing `date`, in the
 * local calendar. The server stores the label as given; this is only the
 * default a fresh draft starts with.
 */
export function weekCycleLabel(date: Date = new Date()): string {
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // getDay(): Sunday is 0; step back to Monday.
  const back = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - back);
  const month = MONTHS[monday.getMonth()]!.slice(0, 3);
  return `Week of ${monday.getDate()} ${month} ${monday.getFullYear()}`;
}
